import styles from "../../styles/results.module.css";
import PersonalInformation from "./PersonalInformation";
import DocumentDetails from "./DocumentDetails";
import { IDocument } from "../../types/document";
import { CircularProgress } from "@mui/material";
import { useCustomerDetailsContext } from "../../provider/CustomerDetailsProvider";
import commonStyles from "../../styles/common.module.css";
import Image from "../common/Image";
import { IBarcodeValues, ISubmitRequestBody } from "../../types/api/application.type";
import {
  ICaptureTimestamp,
  IImageFormat,
  IImageQuality,
  IPrimaryDocumentImages,
  ResultsStep,
} from "../../types/onboard";
import { submitCustomer } from "../../actions/onboard";
import ErrorIcon from "@mui/icons-material/Error";
import { useEffect, useState } from "react";
import { useApiError } from "../../provider/ApiErrorProvider";
import Error from "../common/Error";
import { getUserDevice } from "../../utils/helper";
import { useTranslation } from "react-i18next";
import { useClientSession } from "../../provider/ClientSessionProvider";
import SubmissionConfirmation from "./SubmissionConfirmation";


interface Props {
  captureTimestamp: ICaptureTimestamp;
  imageFormat: IImageFormat;
  imageQuality: IImageQuality;
  primaryDocumentImages:IPrimaryDocumentImages
  setIsExitModalOpen:React.Dispatch<React.SetStateAction<boolean>>
}

const Results = ({
  captureTimestamp,
  imageFormat,
  imageQuality,
  primaryDocumentImages,
  setIsExitModalOpen
}: Props) => {
  const { summary } = useCustomerDetailsContext();
  const [currentStep, setCurrentStep] = useState(1);
  const {errorMessage,setError} = useApiError()
  const [submitResponse,setSubmitResponse] = useState({
    applicationId:""
  });
  const {t,i18n} = useTranslation(); 
  const [isDisabled, setIsDisabled] = useState(false);
  // const PHONE_NUMBER = sessionStorage.getItem("phoneNumber") as string;
  const PD_TYPE = sessionStorage.getItem("PD_Type") as string;
  // Open access: there is no account, so the engine's application id is the
  // correlation key, and no applicant email is available. Phase 7 settles how
  // the Review Center keys and contacts an application.
  const { applicationId } = useClientSession();

  const data: ISubmitRequestBody = {
    userId: applicationId ?? undefined,
    email: undefined,
    // phoneNumber: PHONE_NUMBER,
    userDevice: getUserDevice(),
    primaryDocument: {
      ocrDocumentType: summary?.primaryDocument?.ocrDocumentType || "",
      userInputDocumentType: i18n.getFixedT("en")(PD_TYPE) || "",
      documentNumber:summary?.primaryDocument?.documentNumber || "",
      docAttributes: {
        issuingCountry: summary?.primaryDocument?.docAttributes?.issuingCountry || "",
        issueDate: summary?.primaryDocument?.docAttributes?.issueDate || "",
        expiryDate: summary?.primaryDocument?.docAttributes?.expiryDate  || "",
        edition: summary?.primaryDocument?.docAttributes?.edition || ""
      },
      docAuthDetails: {
        isScreenshot: summary?.primaryDocument?.docAuthDetails?.isScreenshot,
        documentRecognized: summary?.primaryDocument?.docAuthDetails?.documentRecognized,
        barcodeDetected: summary?.primaryDocument?.docAuthDetails?.barcodeDetected
      },
      frontImage: {
        imageData: summary?.primaryDocument?.frontImage?.imageData
          ?  summary?.primaryDocument?.frontImage?.imageData
          : primaryDocumentImages?.frontImage,
        format: imageFormat.documentFront,
        captureTimestamp: captureTimestamp?.documentFront?.toString(),
        quality: imageQuality.documentFront,
      },
      backImage: {
        imageData: summary?.primaryDocument?.backImage?.imageData
          ? summary?.primaryDocument?.backImage?.imageData
          : primaryDocumentImages?.backImage,
        format: imageFormat.documentBack,
        captureTimestamp: captureTimestamp?.documentBack?.toString(),
        quality: imageQuality.documentBack,
      },
      documentPortrait: {
        imageData: summary?.primaryDocument?.documentPortrait?.imageData
          ? summary?.primaryDocument?.documentPortrait?.imageData
          : "",
        format: imageFormat.documentFront,
        captureTimestamp: captureTimestamp?.documentFront?.toString(),
        quality: imageQuality.documentFront,
      },

      idAttributes: {
        firstName: summary?.primaryDocument?.idAttributes?.firstName,
        lastName: summary?.primaryDocument?.idAttributes?.lastName,
        dateOfBirth: summary?.primaryDocument?.idAttributes?.dateOfBirth,
        address: summary?.primaryDocument?.idAttributes?.address,
        sex: summary?.primaryDocument?.idAttributes?.sex,
        eyes:summary?.primaryDocument?.idAttributes?.eyes,
        hair: summary?.primaryDocument?.idAttributes?.hair,
        height: summary?.primaryDocument?.idAttributes?.height,
        weight: summary?.primaryDocument?.idAttributes?.weight,
        nationality: summary?.primaryDocument?.idAttributes?.nationality,
      },
    },
    biometrics: {
      selfie: {
        imageData: summary?.biometrics?.selfie?.imageData || "",
        format: imageFormat.neutral,
        captureTimestamp: captureTimestamp?.neutral?.toString(),
        quality: imageQuality.selfie,
      },
      activeLivenessMedia: {
        imageData:summary?.biometrics?.activeLivenessMedia?.imageData || "",
        format: imageFormat.smileLiveness,
        captureTimestamp: captureTimestamp?.smileLiveness?.toString(),
        quality: imageQuality.smileLiveness,
      },
      faceAuthResult: summary?.biometrics?.faceAuthResult as boolean,
      faceAuthScore: summary?.biometrics?.faceAuthScore as number,
      activeLivenessScore: summary?.biometrics?.activeLivenessScore as number,
      activeLivenessResult: summary?.biometrics?.activeLivenessResult as boolean,
      passiveLivenessResult: summary?.biometrics?.passiveLivenessResult as boolean,
      passiveLivenessScore: summary?.biometrics?.passiveLivenessScore as number,
    },
    applicationStatus: "PENDING",
  };

  function handleSubmit() {
    if (!isDisabled) {
      sessionStorage.removeItem("timerStartTime");
      submitCustomer(data, setCurrentStep, setIsDisabled,setError,setSubmitResponse);
    }
  }

  function handleOnExit() {
    setIsExitModalOpen(true)
  }

    useEffect(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' }); 
    }, [currentStep]);

  const renderStep = (currentStep: ResultsStep) => {
    switch (currentStep) {
      case ResultsStep.REVIEW_DETAILS:
        return (
          <div className={styles.resultsDetails}>
            {!summary ? (
              <div>
                <CircularProgress />
              </div>
            ) : (
              <div className={styles.resultsDetailsContainer}>
                <div>
                  <h1 className={commonStyles.header}>{t('review.header')}</h1>
                  <p className={commonStyles.description}>
                    {t('review.description')}
                  </p>
                </div>

                <div className={commonStyles.instructionBadgeSummary}>
                  <ErrorIcon
                    sx={{
                      color: "var(--color-caution)",
                    }}
                  />
                  <div className={commonStyles.errorHeaderSummary}>
                  {t('review.instructionbadge')}
                  </div>
                </div>
                <p className={styles.sectionHeader}>{t('primarydocument.title')}</p>
                <DocumentDetails
                  primaryDocument = {summary?.primaryDocument}
                  primaryDocumentImages={primaryDocumentImages}
                />
                <div className={styles.divider}></div>
                <div className={styles.selfieContainer}>
                  <p className={styles.sectionHeader}>{t('selfie')}</p>
                  <div className={styles.sectionImageWrapper}>
                    <Image
                      src={`data:image/jpeg;base64,${summary?.biometrics?.selfie?.imageData}`}
                      className={styles.selfieContainerImg}
                    ></Image>
                  </div>
                </div>
                <div className={styles.buttonsContainer}>
                  <button
                    className={styles.button_secondary}
                    onClick={handleOnExit}
                  >
                    {t('button.exit')}
                  </button>
                  <button
                    className={`${isDisabled ? styles.button_primary_disabled : styles.button_primary} `}
                    onClick={handleSubmit}
                  >
                    {t('button.finishandsubmit')}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      case ResultsStep.SUCCESSFULL_SUBMISSION:
        return <SubmissionConfirmation applicationId={submitResponse.applicationId} />;
    }
  };

  return <>{
    errorMessage != null ? 
    <Error  errorMessage={errorMessage}/>
    :
    renderStep(currentStep)
  }</>;
};

export default Results;
